<!-- eslint-disable vue/no-mutating-props -->
<script setup lang="ts">
import type { SiteBlock } from '../../../types/site'

const props = defineProps<{ block: SiteBlock }>()

const t = useSiteEditorT()

const TARGETS = [
  { value: 'blank', label: t('site.f.newsletter.opt_new_tab', 'New tab') },
  { value: 'self', label: t('site.f.newsletter.opt_same_tab', 'Same tab') }
] as const

const addHidden = () => {
  props.block.data.hidden_fields = [...(props.block.data.hidden_fields ?? []), { name: '', value: '' }]
}
const removeHidden = (i: number) => {
  props.block.data.hidden_fields?.splice(i, 1)
}
</script>

<template>
  <!-- eslint-disable vue/no-mutating-props -->
  <UFormField :label="t('site.f.common.heading_optional', 'Heading (optional)')">
    <UInput
      v-model="block.data.headline"
      class="w-full"
    />
  </UFormField>
  <UFormField :label="t('site.f.common.intro_optional', 'Intro (optional)')">
    <UTextarea
      v-model="block.data.subtext"
      :rows="2"
      class="w-full"
    />
  </UFormField>

  <UFormField
    :label="t('site.f.common.submit_url_label', 'Where submissions go')"
    :help="t('site.f.newsletter.email_addresses_are_sent_directly_to_thi_help', 'Email addresses are sent directly to this URL. They are not stored on this platform and we cannot see or recover them — you are responsible for the service you point this at, and your privacy policy should name it. Must start with https://.')"
    :error="!block.data.action_url ? t('site.f.newsletter.err_action_url', 'This block needs a destination URL before the page can be saved.') : undefined"
  >
    <UInput
      v-model="block.data.action_url"
      :placeholder="t('site.f.common.formspree_placeholder', 'https://formspree.io/f/...')"
      class="w-full"
    />
  </UFormField>

  <UFormField
    :label="t('site.f.newsletter.email_field_name', 'Email field name')"
    :help="t('site.f.newsletter.mailchimp_expects_email_most_other_servi_help', 'Mailchimp expects EMAIL; most other services accept email.')"
  >
    <UInput
      :model-value="block.data.email_field_name ?? ''"
      :placeholder="t('site.f.newsletter.email', 'email')"
      class="w-full"
      @update:model-value="block.data.email_field_name = String($event)"
    />
  </UFormField>

  <div class="grid grid-cols-2 gap-2">
    <UFormField :label="t('site.f.newsletter.placeholder', 'Placeholder')">
      <UInput
        :model-value="block.data.placeholder ?? ''"
        :placeholder="t('site.f.newsletter.you_example_com', 'you@example.com')"
        class="w-full"
        @update:model-value="block.data.placeholder = String($event)"
      />
    </UFormField>
    <UFormField :label="t('site.f.common.button_text', 'Button text')">
      <UInput
        :model-value="block.data.button_text ?? ''"
        :placeholder="t('site.f.newsletter.subscribe', 'Subscribe')"
        class="w-full"
        @update:model-value="block.data.button_text = String($event)"
      />
    </UFormField>
  </div>

  <SiteEditorFieldGroup :label="t('site.f.common.advanced_label', 'Advanced')">
    <UFormField
      :label="t('site.f.common.after_submit_label', 'After submitting, open')"
      :help="t('site.f.common.open_target_help', 'Choose Same tab if your provider redirects back to your site.')"
    >
      <SiteEditorOptionToggle
        :options="TARGETS"
        :model-value="block.data.target ?? 'blank'"
        @update:model-value="block.data.target = $event as 'self' | 'blank'"
      />
    </UFormField>

    <UFormField
      :label="t('site.f.common.confirmation_message_label', 'Confirmation message')"
      :help="t('site.f.common.confirmation_help', 'Shown after the visitor submits. We cannot tell whether the provider accepted it, so keep this neutral.')"
    >
      <UInput
        :model-value="block.data.success_text ?? ''"
        :placeholder="t('site.f.newsletter.thanks_please_check_your_inbox', 'Thanks — please check your inbox.')"
        class="w-full"
        @update:model-value="block.data.success_text = String($event)"
      />
    </UFormField>

    <UFormField
      :label="t('site.f.common.consent_label', 'Consent checkbox (optional)')"
      :help="t('site.f.common.consent_help', 'When set, the visitor must tick this before submitting.')"
    >
      <UTextarea
        :model-value="block.data.consent_text ?? ''"
        :rows="2"
        :placeholder="t('site.f.newsletter.i_agree_to_receive_emails_and_accept_the', 'I agree to receive emails and accept the privacy policy.')"
        class="w-full"
        @update:model-value="block.data.consent_text = String($event)"
      />
    </UFormField>

    <UFormField
      :label="t('site.f.common.hidden_fields_label', 'Hidden fields')"
      :help="t('site.f.newsletter.extra_values_some_providers_require_mail_help', 'Extra values some providers require — Mailchimp\'s u, id and b_… bot trap, or Formspree\'s _subject and _next.')"
    >
      <div class="space-y-2">
        <div
          v-for="(hidden, i) in block.data.hidden_fields ?? []"
          :key="i"
          class="flex items-center gap-1.5"
        >
          <UInput
            v-model="hidden.name"
            :placeholder="t('site.f.common.name_placeholder', 'name')"
            class="w-full"
          />
          <UInput
            :model-value="hidden.value ?? ''"
            :placeholder="t('site.f.common.value_placeholder', 'value')"
            class="w-full"
            @update:model-value="hidden.value = String($event)"
          />
          <UButton
            size="xs"
            variant="ghost"
            color="error"
            icon="i-lucide-x"
            @click="removeHidden(i)"
          />
        </div>
        <UButton
          size="xs"
          variant="outline"
          color="neutral"
          icon="i-lucide-plus"
          @click="addHidden"
        >
          {{ t('site.f.common.add_hidden_field', 'Add hidden field') }}
        </UButton>
      </div>
    </UFormField>
  </SiteEditorFieldGroup>
</template>